import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Directive,
  ElementRef,
  afterNextRender,
  computed,
  contentChildren,
  effect,
  inject,
  input,
  model,
  untracked,
  viewChild,
} from '@angular/core';

/** Markeert een slide; de carrousel telt en scrolt op deze elementen. */
@Directive({
  selector: '[eCarouselSlide]',
  host: { class: 'e-carousel__slide', role: 'group', 'aria-roledescription': 'slide' },
})
export class ECarouselSlideDirective {
  readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
}

export interface ECarouselLabels {
  readonly previous: string;
  readonly next: string;
  /** Met `{{index}}` en `{{count}}`. */
  readonly slide: string;
}

const DEFAULT_LABELS: ECarouselLabels = { previous: 'Previous', next: 'Next', slide: 'Item {{index}} of {{count}}' };

/**
 * Generieke horizontale carrousel met CSS scroll-snap: geprojecteerde slides
 * (`eCarouselSlide`), stippen, vorige/volgende en pijltoetsen. `index` is een
 * model: de host kan hem zetten (scrolt naar de slide) en leest de slide die
 * in beeld is (IntersectionObserver op de strook). Teksten komen van de host
 * (`labels`), zodat de component geen vertaalscope kent.
 */
@Component({
  selector: 'e-carousel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'e-carousel', role: 'group', 'aria-roledescription': 'carousel', '[attr.aria-label]': 'ariaLabel()' },
  styles: `
    :host { display: block; }
    .e-carousel__track {
      display: flex; overflow-x: auto; scroll-snap-type: x mandatory; scrollbar-width: none; outline: none;
      -webkit-overflow-scrolling: touch;
    }
    .e-carousel__track::-webkit-scrollbar { display: none; }
    .e-carousel__track:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
    ::ng-deep .e-carousel__slide { flex: 0 0 100%; scroll-snap-align: start; box-sizing: border-box; min-width: 0; }
    .e-carousel__nav { display: flex; align-items: center; justify-content: center; gap: 12px; margin-top: 6px; }
    .e-carousel__arrow { border: 0; background: none; cursor: pointer; color: var(--text-secondary); padding: 4px 8px; font: inherit; }
    .e-carousel__arrow:disabled { opacity: 0.3; cursor: default; }
    .e-carousel__dots { display: flex; gap: 6px; }
    .e-carousel__dot {
      width: 8px; height: 8px; border-radius: 999px; border: 0; padding: 0;
      background: var(--border-color, rgba(0, 0, 0, 0.2)); cursor: pointer; transition: width 0.15s ease;
    }
    .e-carousel__dot.is-active { width: 20px; background: var(--primary); }
  `,
  template: `
    <div #track class="e-carousel__track" tabindex="0" (keydown.arrowRight)="step(1)" (keydown.arrowLeft)="step(-1)"><ng-content /></div>
    @if (count() > 1) {
      <div class="e-carousel__nav">
        <button type="button" class="e-carousel__arrow" data-test-id="carousel-previous" [disabled]="index() === 0" [attr.aria-label]="labels().previous" (click)="step(-1)">
          <i class="fas fa-chevron-left" aria-hidden="true"></i>
        </button>
        <div class="e-carousel__dots">
          @for (i of indices(); track i) {
            <button
              type="button"
              class="e-carousel__dot"
              data-test-id="carousel-dot"
              [class.is-active]="i === index()"
              [attr.aria-label]="slideLabel(i)"
              [attr.aria-current]="i === index() ? 'true' : null"
              (click)="goTo(i)"
            ></button>
          }
        </div>
        <button type="button" class="e-carousel__arrow" data-test-id="carousel-next" [disabled]="index() === count() - 1" [attr.aria-label]="labels().next" (click)="step(1)">
          <i class="fas fa-chevron-right" aria-hidden="true"></i>
        </button>
      </div>
    }
  `,
})
export class ECarouselComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly track = viewChild.required<ElementRef<HTMLElement>>('track');
  private readonly slides = contentChildren(ECarouselSlideDirective);

  /** Index van de getoonde slide (0-based); van buiten zetten scrolt ernaartoe. */
  readonly index = model(0);
  readonly labels = input<ECarouselLabels>(DEFAULT_LABELS);
  readonly ariaLabel = input<string | undefined>(undefined);

  readonly count = computed(() => this.slides().length);
  protected readonly indices = computed(() => Array.from({ length: this.count() }, (_, i) => i));

  private observer: IntersectionObserver | undefined;
  /** Programmatische scroll bezig: de observer mag de index dan niet terugzetten. */
  private scrolling = false;
  private scrollTimer: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    // Index (van buiten of via knoppen) → naar de slide scrollen.
    effect(() => {
      const i = this.index();
      const slide = untracked(() => this.slides())[i]?.element.nativeElement;
      if (!slide) {
        return;
      }
      this.scrolling = true;
      this.track().nativeElement.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => (this.scrolling = false), 450);
    });

    // Vegen → index bijwerken op de slide die grotendeels in beeld is.
    afterNextRender(() => {
      if (typeof IntersectionObserver === 'undefined') {
        return;
      }
      this.observer = new IntersectionObserver(
        entries => {
          if (this.scrolling) {
            return;
          }
          const visible = entries
            .filter(entry => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
          if (!visible) {
            return;
          }
          const i = this.slides().findIndex(slide => slide.element.nativeElement === visible.target);
          if (i >= 0 && i !== this.index()) {
            this.index.set(i);
          }
        },
        { root: this.track().nativeElement, threshold: 0.6 },
      );
      this.slides().forEach(slide => this.observer?.observe(slide.element.nativeElement));
    });

    this.destroyRef.onDestroy(() => {
      this.observer?.disconnect();
      clearTimeout(this.scrollTimer);
    });
  }

  protected step(delta: number): void {
    this.goTo(Math.min(this.count() - 1, Math.max(0, this.index() + delta)));
  }

  protected goTo(i: number): void {
    this.index.set(i);
  }

  protected slideLabel(i: number): string {
    return this.labels().slide.replace('{{index}}', String(i + 1)).replace('{{count}}', String(this.count()));
  }
}
